import type { ReportingTaskImportJob } from '@engagement-web-app/common/reporting';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useState } from 'react';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { fetchReportingTaskImport, fetchReportingTaskStatuses, triggerReportingTaskImport } from '@/queries/reporting';

/*
 * Import job statuses that mean the pipeline is still running (the button stays
 * disabled and the view polls until a terminal status).
 */
const IN_PROGRESS_IMPORT_STATUSES: readonly string[] = ['TRIGGERED', 'IMPORT_COMPLETED', 'SUMMARIZE_COMPLETED'];

export function useTaskStatusHistory(taskId: string) {
    return useQuery({
        queryKey: ['tasks', taskId, 'statuses'],
        queryFn: () => fetchReportingTaskStatuses(taskId),
    });
}

export type SurveyImportState = {
    importJob: ReportingTaskImportJob | null;
    importInProgress: boolean;
    importSucceeded: boolean;
    importFailed: boolean;
    targetDate: string;
    setTargetDate: (value: string) => void;
    triggerImport: () => void;
    isTriggering: boolean;
};

/**
 * The latest survey-import job (polled while running) and the trigger flow with
 * its derived phase flags and button label.
 */
export function useSurveyImport(taskId: string, surveyDate: string | null): SurveyImportState {
    const queryClient = useQueryClient();

    const { data: importData } = useQuery({
        queryKey: ['tasks', taskId, 'import'],
        queryFn: () => fetchReportingTaskImport(taskId),
        // Poll while the pipeline is running so the status updates on its own.
        refetchInterval: (query) =>
            IN_PROGRESS_IMPORT_STATUSES.includes(query.state.data?.job?.status ?? '') ? 5000 : false,
    });

    const importJob = importData?.job ?? null;
    const importInProgress = !!importJob && IN_PROGRESS_IMPORT_STATUSES.includes(importJob.status);
    const importSucceeded = importJob?.status === 'COMMENT_ANALYSIS_COMPLETED';
    const importFailed = !!importJob && !importInProgress && !importSucceeded;

    // Pre-fill with the imported survey's month so a re-import keeps it, or the
    // current month for a survey that has not been imported yet.
    const [targetDate, setTargetDate] = useState(() =>
        format(surveyDate ? new Date(surveyDate) : new Date(), 'yyyy-MM')
    );
    const importMutation = useMutation({
        mutationFn: () => triggerReportingTaskImport(taskId, { targetDate: targetDate || undefined }),
        onSuccess: () => {
            showSuccessToast({
                title: 'サーベイのインポート',
                description: 'サーベイのインポートを開始しました。',
            });
            queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'import'] });
        },
        onError: () => {
            showErrorToast({
                title: 'サーベイのインポート',
                description: 'サーベイのインポートに失敗しました。時間をおいて再度お試しください。',
            });
        },
    });

    return {
        importJob,
        importInProgress,
        importSucceeded,
        importFailed,
        targetDate,
        setTargetDate,
        triggerImport: () => importMutation.mutate(),
        isTriggering: importMutation.isPending,
    };
}
